import React from 'react';

import { css, cx } from '@emotion/css';
import { useFormContext, useWatch } from 'react-hook-form';

import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { FilePreview } from '@/uiKit/atoms/FilePreview.tsx';
import { MessageData } from '@/uiKit/molecules/chat/MessageDataSchema.ts';

export type MessageAttachmentsProps = {
  className?: string;
};

/**
 * MessageAttachments is a React functional component that displays a list of uploaded file attachments.
 *
 * This component retrieves and observes the current attachments from the form context using `useFormContext`
 * and `useWatch`. It conditionally renders a list of file previews if attachments are present.
 *
 * Each file preview includes functionality to remove the corresponding attachment
 * from the list by updating the form state.
 *
 * Props:
 * - className: An optional string to apply additional custom CSS classes to the wrapper element.
 *
 * Dependencies:
 * - `useFormContext` and `useWatch` from React Hook Form are used to manage and observe the attachment data.
 * - `useStyles` is called with `makeStyles` to generate CSS styles for this component.
 *
 * Behavior:
 * - Renders null if there are no attachments available.
 * - Displays each attachment as a clickable `FilePreview` element, allowing users to remove the file from the list.
 */
export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  className,
}) => {
  const styles = useStyles(makeStyles);
  const formContext = useFormContext<MessageData>();
  const attachments = useWatch({
    name: 'contentAttachment',
    control: formContext.control,
  });

  if (attachments.length <= 0) {
    return null;
  }
  return (
    <div className={cx(styles.attachments, className)}>
      {attachments.map((uploadedFile, i) => (
        <FilePreview
          name={uploadedFile.name}
          key={`${uploadedFile.name}-${i}`}
          className={styles.attachmentFile}
          onClick={() =>
            formContext.setValue(
              'contentAttachment',
              attachments.filter(file => file !== uploadedFile),
              {
                shouldValidate: true,
              },
            )
          }
        />
      ))}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  attachments: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    width: '100%',
    overflowX: 'auto',
    gap: theme.spacing(4),
    padding: theme.spacing(1, 8),
    scrollbarColor: 'lightgrey transparent',
    '::-webkit-scrollbar': {
      backgroundColor: 'transparent',
    },
  }),
  attachmentFile: css({
    flex: '0 1 auto',
  }),
});
